import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { todayISO } from "@/lib/format";

export const PackageDialog = ({ open, onOpenChange, pkg, presetOwnerId, presetDogId, onSaved }) => {
  const editing = !!pkg;
  const [owners, setOwners] = useState([]);
  const [dogs, setDogs] = useState([]);
  const [methods, setMethods] = useState(["Contanti", "Carta/POS", "Bonifico", "Altro"]);
  const [form, setForm] = useState({ owner_id: "", dog_id: "", opening_date: todayISO(), total_price: 350, total_credits: 10, notes: "" });
  const [payMode, setPayMode] = useState("none");
  const [pay, setPay] = useState({ amount: "", date: "", method: "Contanti", notes: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/owners").then((r) => setOwners(r.data)).catch(() => {});
    api.get("/dogs").then((r) => setDogs(r.data)).catch(() => {});
    api.get("/settings").then((r) => {
      const pm = (r.data.payment_methods || [])
        .map((m) => (typeof m === "string" ? { name: m, active: true } : m))
        .filter((m) => m && m.active !== false)
        .map((m) => m.name);
      if (pm.length) setMethods(pm);
    }).catch(() => {});
    if (pkg) setForm({ ...pkg });
    else setForm({
      owner_id: presetOwnerId || "", dog_id: presetDogId || "",
      opening_date: todayISO(), total_price: 350, total_credits: 10, notes: "",
    });
    setPayMode("none");
    setPay({ amount: "", date: "", method: "Contanti", notes: "" });
  }, [open, pkg, presetOwnerId, presetDogId]);

  const dogsForOwner = dogs.filter((d) => !form.owner_id || d.owner_id === form.owner_id);

  const onDog = (v) => {
    const d = dogs.find((x) => x.id === v);
    setForm((f) => ({ ...f, dog_id: v, owner_id: d ? d.owner_id : f.owner_id }));
  };

  const submit = async () => {
    if (!form.dog_id || !form.owner_id) return toast.error("Seleziona cane e proprietario.");
    const total = Number(form.total_price);
    let initial_payment = null;
    if (!editing && payMode !== "none") {
      const amount = payMode === "full" ? total : Number(pay.amount);
      if (!(amount > 0)) return toast.error("Inserisci un importo di acconto valido.");
      if (amount > total + 0.01) return toast.error("Il pagamento non può superare il totale del pacchetto.");
      initial_payment = { amount, method: pay.method || "Contanti", date: pay.date || form.opening_date, notes: pay.notes || "" };
    }
    setSaving(true);
    try {
      const payload = {
        dog_id: form.dog_id, owner_id: form.owner_id, opening_date: form.opening_date,
        total_price: total, total_credits: Number(form.total_credits), notes: form.notes || "",
      };
      if (initial_payment) payload.initial_payment = initial_payment;
      const res = editing ? await api.put(`/packages/${pkg.id}`, payload) : await api.post("/packages", payload);
      toast.success(editing ? "Pacchetto aggiornato" : (initial_payment ? "Pacchetto creato e pagamento registrato" : "Pacchetto creato"));
      onSaved?.(res.data);
      onOpenChange(false);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle className="font-head">{editing ? "Modifica pacchetto" : "Nuovo pacchetto"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1">
            <Label>Cane *</Label>
            <Select value={form.dog_id} onValueChange={onDog} disabled={!!presetDogId && !editing}>
              <SelectTrigger data-testid="pkg-dog"><SelectValue placeholder="Seleziona cane…" /></SelectTrigger>
              <SelectContent>
                {dogsForOwner.map((d) => <SelectItem key={d.id} value={d.id}>{d.nome} — {d.owner_name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>Data apertura</Label>
              <Input type="date" data-testid="pkg-date" value={form.opening_date} onChange={(e) => setForm((f) => ({ ...f, opening_date: e.target.value }))} />
            </div>
            <div className="space-y-1">
              <Label>Crediti inclusi</Label>
              <Input type="number" data-testid="pkg-credits" value={form.total_credits} onChange={(e) => setForm((f) => ({ ...f, total_credits: e.target.value }))} />
            </div>
          </div>
          <div className="space-y-1">
            <Label>Prezzo totale (€)</Label>
            <Input type="number" data-testid="pkg-price" value={form.total_price} onChange={(e) => setForm((f) => ({ ...f, total_price: e.target.value }))} />
            <p className="text-xs text-muted-foreground">Il prezzo resta storicizzato su questo pacchetto.</p>
          </div>
          <div className="space-y-1">
            <Label>Note</Label>
            <Textarea data-testid="pkg-notes" value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} />
          </div>

          {!editing && (
            <div className="space-y-2 rounded-xl border border-border p-3 bg-secondary/40" data-testid="pkg-payment-section">
              <Label>Pagamento</Label>
              <Select value={payMode} onValueChange={setPayMode}>
                <SelectTrigger data-testid="pkg-pay-mode"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nessun pagamento</SelectItem>
                  <SelectItem value="full">Pagato interamente</SelectItem>
                  <SelectItem value="acconto">Acconto</SelectItem>
                </SelectContent>
              </Select>
              {payMode !== "none" && (
                <div className="grid grid-cols-2 gap-3 pt-1">
                  <div className="space-y-1">
                    <Label>Importo (€)</Label>
                    <Input type="number" data-testid="pkg-pay-amount"
                      value={payMode === "full" ? Number(form.total_price) : pay.amount}
                      disabled={payMode === "full"}
                      onChange={(e) => setPay((p) => ({ ...p, amount: e.target.value }))} />
                  </div>
                  <div className="space-y-1">
                    <Label>Data pagamento</Label>
                    <Input type="date" data-testid="pkg-pay-date" value={pay.date || form.opening_date}
                      onChange={(e) => setPay((p) => ({ ...p, date: e.target.value }))} />
                  </div>
                  <div className="space-y-1">
                    <Label>Metodo</Label>
                    <Select value={pay.method} onValueChange={(v) => setPay((p) => ({ ...p, method: v }))}>
                      <SelectTrigger data-testid="pkg-pay-method"><SelectValue /></SelectTrigger>
                      <SelectContent>{methods.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label>Nota pagamento</Label>
                    <Input data-testid="pkg-pay-notes" value={pay.notes} onChange={(e) => setPay((p) => ({ ...p, notes: e.target.value }))} />
                  </div>
                  {payMode === "acconto" && <p className="col-span-2 text-xs text-muted-foreground">Le rate successive si registrano normalmente da "Registra pagamento".</p>}
                </div>
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
          <Button data-testid="pkg-save" onClick={submit} disabled={saving}>{saving ? "Salvataggio…" : "Salva"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
